const WEBFLOW_API_URL = 'https://api.webflow.com/v2';

/**
 * Obtiene items de una colección usando Firebase Functions como proxy
 */
export const fetchCollectionItemsProxy = async (collectionId) => {
  const functionsUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL;
  const useProxy = import.meta.env.VITE_USE_PROXY !== 'false';
  
  try {
    let url;
    let options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    
    if (useProxy && functionsUrl) {
      // Usar Firebase Functions como proxy
      const siteId = import.meta.env.VITE_WEBFLOW_SITE_ID;
      url = `${functionsUrl}/getWebflowData?collectionId=${collectionId}${siteId ? `&siteId=${siteId}` : ''}`;
      console.error('[DEBUG] Fetching from:', url);
    } else {
      // Modo desarrollo: usar datos mock o API directa
      console.error('[DEBUG] Using mock data - useProxy:', useProxy, 'functionsUrl:', functionsUrl);
      return getMockData();
    }
    
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[DEBUG] Response error:', response.status, errorText);
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    
    const data = await response.json();
    console.error('[DEBUG] Received data:', {
      itemsCount: data.items?.length || 0,
      total: data.total,
      hasItems: !!data.items
    });
    return data.items || [];
  } catch (error) {
    console.error('[DEBUG] Error in fetchCollectionItemsProxy:', error);
    // En desarrollo, retornar datos mock si falla
    if (import.meta.env.DEV) {
      console.error('[DEBUG] Returning mock data due to error');
      return getMockData();
    }
    throw error;
  }
};

/**
 * Transformar datos de Webflow API a formato interno
 */
export const transformWebflowData = (items) => {
  if (!items || !Array.isArray(items)) {
    console.error('[DEBUG] transformWebflowData: items is not an array', items);
    return [];
  }

  console.error('[DEBUG] transformWebflowData: processing', items.length, 'items');
  
  const transformed = items
    .map((item, index) => {
      const fieldData = item.fieldData || {};
      
      // Mapeo de campos con múltiples variantes posibles
      const name = fieldData.Name || fieldData.name || fieldData.title || '';
      const direccion = fieldData.Direccion || fieldData.direccion || fieldData.address || '';
      const ciudad = fieldData.Ciudad || fieldData.ciudad || fieldData.city || '';
      
      // Extraer ID del servicio (puede venir como objeto de relación o como ID directo)
      let servicioId = '';
      const servicioField = fieldData.servicio || fieldData.service || fieldData.category || '';
      if (typeof servicioField === 'object' && servicioField !== null) {
        // Si es un objeto de relación, extraer el ID
        servicioId = servicioField.id || servicioField._id || servicioField || '';
      } else {
        // Si es un string/ID directo
        servicioId = servicioField.toString();
      }
      
      const lat = (fieldData.Lat || fieldData.lat || fieldData.latitude || fieldData.Latitude || '').toString().trim();
      const lng = (fieldData.Long || fieldData.long || fieldData.lng || fieldData.longitude || fieldData.Longitude || '').toString().trim();
      
      // Extraer ID del department (puede venir como objeto de relación o como ID directo)
      let departmentId = '';
      const departmentField = fieldData.department || fieldData.Department || fieldData.departamento || '';
      if (typeof departmentField === 'object' && departmentField !== null) {
        // Si es un objeto de relación, extraer el ID
        departmentId = departmentField.id || departmentField._id || departmentField || '';
      } else {
        // Si es un string/ID directo
        departmentId = departmentField.toString();
      }
      
      // Convertir coordenadas a números (limpiar espacios)
      const latNum = parseFloat(lat.replace(/\s+/g, ''));
      const lngNum = parseFloat(lng.replace(/\s+/g, ''));
      
      // Validar que las coordenadas sean válidas
      if (isNaN(latNum) || isNaN(lngNum) || latNum === 0 || lngNum === 0) {
        console.error(`[DEBUG] Item ${index} filtered out - invalid coordinates: lat=${latNum}, lng=${lngNum}`, {
          name,
          lat,
          lng,
          fieldData
        });
        return null;
      }
      
      console.error(`[DEBUG] Item ${index} transformed:`, {
        name,
        servicioId,
        departmentId,
        servicioField,
        departmentField
      });
      
      return {
        id: item.id || item['Item ID'],
        title: name,
        description: direccion,
        address: direccion,
        city: ciudad,
        service: servicioId, // Guardar como ID
        category: servicioId, // Guardar como ID
        lat: latNum,
        lng: lngNum,
        department: departmentId, // Guardar como ID
        slug: fieldData.Slug || fieldData.slug || '',
        archived: fieldData.Archived === 'true' || fieldData.archived === true,
        draft: fieldData.Draft === 'true' || fieldData.draft === true,
      };
    })
    .filter(item => item !== null);
  
  console.error('[DEBUG] transformWebflowData: transformed', transformed.length, 'items');
  return transformed;
};

/**
 * Datos mock para desarrollo
 */
function getMockData() {
  return [
    {
      id: '1',
      fieldData: {
        Name: 'Peaje San Juan',
        Direccion: 'Vía La Paz',
        Ciudad: 'La Guajira',
        servicio: 'peaje',
        Lat: '10.799994616780713',
        Long: '-72.97541115936852',
        department: 'la-guajira'
      }
    },
    {
      id: '2',
      fieldData: {
        Name: 'Parqueadero Centro',
        Direccion: 'Calle 10 # 50-30',
        Ciudad: 'Medellín',
        servicio: 'parqueadero',
        Lat: '6.244809132',
        Long: '-75.58016316',
        department: 'antioquia'
      }
    },
    {
      id: '3',
      fieldData: {
        Name: 'Lavautos Express',
        Direccion: 'Carrera 50 # 32-16',
        Ciudad: 'Medellín',
        servicio: 'lava-autos',
        Lat: '6.234009246313306',
        Long: '-75.57302098939887',
        department: 'antioquia'
      }
    },
    {
      id: '4',
      fieldData: {
        Name: 'Parquímetro El Poblado',
        Direccion: 'Carrera 43A # 1 Sur 150',
        Ciudad: 'Medellín',
        servicio: 'parquimetros',
        Lat: '6.201865802269783',
        Long: '-75.57195212455491',
        department: 'antioquia'
      }
    },
    {
      id: '5',
      fieldData: {
        Name: 'Estación de Servicio Primax',
        Direccion: 'Autopista Norte Km 18',
        Ciudad: 'Chía',
        servicio: 'gasolina',
        Lat: '4.832176689938563',
        Long: '-74.0315248652282',
        department: 'cundinamarca'
      }
    },
    {
      id: '6',
      fieldData: {
        Name: 'Punto de Venta Aeropark',
        Direccion: 'Vereda La Convención, sector aeropuerto',
        Ciudad: 'Rionegro',
        servicio: 'punto-de-venta',
        Lat: '6.172267865620761',
        Long: '-75.43251224477385',
        department: 'antioquia'
      }
    }
  ];
}

/**
 * Transformar datos de servicios de Webflow
 */
export const transformServicesData = (items) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const fieldData = item.fieldData || {};
      
      const name = fieldData.Name || fieldData.name || '';
      // Extraer URL del icono (puede venir como objeto con url o como string directo)
      let icon = '';
      const iconField = fieldData.icon || fieldData.Icon || '';
      if (typeof iconField === 'object' && iconField !== null) {
        icon = iconField.url || iconField.URL || iconField || '';
      } else {
        icon = iconField || '';
      }
      console.error('[DEBUG] transformServicesData: icon extracted:', { name, icon, iconField });
      
      // Extraer color del campo 'color-2' (formato: hsla(...))
      const color = fieldData['color-2'] || fieldData.color || fieldData.Color || '';
      console.error('[DEBUG] transformServicesData: color extracted:', { name, color, colorField: fieldData['color-2'] || fieldData.color || fieldData.Color });
      
      // Extraer order para ordenamiento
      const order = fieldData.order || fieldData.Order || 0;
      const orderNum = typeof order === 'number' ? order : parseInt(order, 10) || 0;
      
      const id = item.id || '';
      const slug = fieldData.Slug || fieldData.slug || '';
      const showOnMap = fieldData['show-on-map'];

      if (!name || !id) {
        return null;
      }
      
      if (showOnMap !== true && showOnMap !== 'true') {
        return null;
      }
      
      return {
        id: id,
        name: name,
        icon: icon,
        color: color,
        order: orderNum,
        slug: slug,
        archived: item.isArchived || fieldData.Archived === 'true' || fieldData.archived === true,
        draft: item.isDraft || fieldData.Draft === 'true' || fieldData.draft === true,
      };
    })
    .filter(item => item !== null && !item.archived && !item.draft);
};

/**
 * Transformar datos de ciudades de Webflow
 */
export const transformCitiesData = (items) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const fieldData = item.fieldData || {};
      
      const name = fieldData.Name || fieldData.name || '';
      const id = item.id || '';
      const slug = fieldData.Slug || fieldData.slug || '';

      if (!name || !id) {
        return null;
      }
      
      return {
        id: id,
        name: name,
        slug: slug,
        archived: item.isArchived || fieldData.Archived === 'true' || fieldData.archived === true,
        draft: item.isDraft || fieldData.Draft === 'true' || fieldData.draft === true,
      };
    })
    .filter(item => item !== null && !item.archived && !item.draft);
};

